import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Form, Spinner } from 'react-bootstrap';
import { YSheet } from '../../../ui/sheet/YSheet';
import { FaBullhorn, FaPlus, FaSearch } from 'react-icons/fa';
import { boardService } from '../services/boardService';
import RichTextEditorBasic from '../../../components/common/RichTextEditorBasic';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YPagination } from '../../../ui/YPagination';
import { BOARD_TAG_KEYS, getBoardTagConfig } from '../board/boardTagConfig';
import {
  formatBoardApiError,
  isThreadExpanded,
  stripHtmlToText,
} from '../board/boardDisplayUtils';
import { ClubBoardPostCard } from './ClubBoardPostCard';
import { ClubAnnouncementDialog } from './ClubAnnouncementDialog';
import { notifyClubBoardRead } from '../hooks/useClubBoardUnread';
import './ClubBoardPage.css';
import './clubBoardFieldEdition.css';

function parseBoardTagFilter(raw) {
  const key = (raw || '').trim().toLowerCase();
  if (!key || key === 'all') return 'all';
  return BOARD_TAG_KEYS.includes(key) ? key : 'all';
}

export default function ClubBoardPage({
  embedded = false,
  clubId: clubIdProp,
  isPresident = false,
  clubName = '',
  memberCount = null,
}) {
  const { clubId: clubIdParam } = useParams();
  const clubId = clubIdProp ?? clubIdParam;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useCurrentUser();
  const [q, setQ] = useState('');
  const [activeTag, setActiveTag] = useState(() => parseBoardTagFilter(searchParams.get('boardTag')));

  const boardTagParam = searchParams.get('boardTag');
  useEffect(() => {
    setActiveTag(parseBoardTagFilter(boardTagParam));
  }, [boardTagParam]);

  useEffect(() => {
    if (clubId == null || clubId === '') return undefined;
    let cancelled = false;
    (async () => {
      try {
        await boardService.markBoardRead(clubId);
        if (!cancelled) notifyClubBoardRead();
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId]);
  const [threads, setThreads] = useState([]);
  const [count, setCount] = useState(0);
  const limit = 20;
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newTag, setNewTag] = useState('general');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const didAutoExpandPinned = useRef(false);
  const bodyEditorRef = useRef(null);
  const fetchRequestId = useRef(0);

  const tagFilters = useMemo(
    () => [{ key: 'all', label: 'All' }, ...BOARD_TAG_KEYS.map((key) => getBoardTagConfig(key))],
    []
  );

  const syncBoardTagInUrl = useCallback(
    (tagKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (embedded) next.set('tab', 'board');
          if (!tagKey || tagKey === 'all') next.delete('boardTag');
          else next.set('boardTag', tagKey);
          return next;
        },
        { replace: true }
      );
    },
    [embedded, setSearchParams]
  );

  const fetchThreads = useCallback(
    async ({ tag = activeTag, pageOffset = offset, searchQ = q } = {}) => {
      if (clubId == null || clubId === '') return;
      const tags = tag === 'all' ? [] : [tag];
      const requestId = ++fetchRequestId.current;
      setLoading(true);
      setError('');
      try {
        const res = await boardService.listThreads(clubId, {
          q: searchQ,
          tags,
          limit,
          offset: pageOffset,
        });
        if (requestId !== fetchRequestId.current) return;
        setThreads(res.results || []);
        setCount(res.count || 0);
      } catch {
        if (requestId !== fetchRequestId.current) return;
        setError('Failed to load posts');
      } finally {
        if (requestId === fetchRequestId.current) setLoading(false);
      }
    },
    [clubId, q, activeTag, limit, offset]
  );

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const handleSearchChange = (e) => {
    setQ(e.target.value);
    setOffset(0);
  };

  const handleTagFilter = (tagKey) => {
    setActiveTag(tagKey);
    syncBoardTagInUrl(tagKey);
    setOffset(0);
    setExpandedIds([]);
  };

  useEffect(() => {
    if (didAutoExpandPinned.current || threads.length === 0) return;
    const pinned = threads.find((t) => t.pinned);
    if (!pinned) return;
    didAutoExpandPinned.current = true;
    setExpandedIds([pinned.id]);
  }, [threads]);

  const handleUploadImage = useCallback(async (file) => {
    if (!file || newImages.length >= 2) return;
    setUploading(true);
    try {
      const init = await boardService.initBoardImageUpload({
        clubId,
        filename: file.name,
        contentType: file.type || 'image/jpeg',
      });
      await fetch(init.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });
      setNewImages((prev) => [...prev, init.object_key].slice(0, 2));
    } catch {
      setError('Failed to upload image');
    } finally {
      setUploading(false);
    }
  }, [clubId, newImages.length]);

  const resetNewPostForm = () => {
    setNewTitle('');
    setNewTag('general');
    setNewBody('');
    setNewImages([]);
  };

  const closeNewPost = () => {
    setShowNew(false);
    resetNewPostForm();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!clubId) {
      setError('Club not found. Refresh the page and try again.');
      return;
    }
    const bodyHtml = (bodyEditorRef.current?.getHtml() ?? newBody).trim();
    if (!newTitle.trim() || !stripHtmlToText(bodyHtml)) {
      setError('Add a title and message before posting.');
      return;
    }
    setCreating(true);
    setError('');
    const tagToPost = newTag || 'general';
    try {
      await boardService.createThread(clubId, {
        title: newTitle.trim(),
        tags: [tagToPost],
        first_post: { body_html: bodyHtml, images: newImages },
      });
      closeNewPost();
      setActiveTag(tagToPost);
      syncBoardTagInUrl(tagToPost);
      setOffset(0);
      setExpandedIds([]);
      try {
        await fetchThreads({ tag: tagToPost, pageOffset: 0 });
        await boardService.markBoardRead(clubId);
        notifyClubBoardRead();
      } catch {
        setError('Post created, but the list could not be refreshed. Reload the board.');
      }
    } catch (err) {
      setError(formatBoardApiError(err, 'Failed to create post'));
    } finally {
      setCreating(false);
    }
  };

  const toggleExpand = useCallback((threadId) => {
    setExpandedIds((prev) => {
      if (isThreadExpanded(prev, threadId)) {
        return prev.filter((id) => !isThreadExpanded([id], threadId));
      }
      return [...prev, threadId];
    });
  }, []);

  const handleReplyPosted = async () => {
    await fetchThreads();
  };

  const boardMain = (
    <>
      <div className="club-board-toolbar">
        <div className="club-board-search">
          <FaSearch className="club-board-search__icon" aria-hidden="true" />
          <YInput
            type="search"
            placeholder="Search posts…"
            value={q}
            onChange={handleSearchChange}
            aria-label="Search board posts"
          />
        </div>
        <div className="club-board-toolbar__actions">
          {isPresident ? (
            <YButton
              type="button"
              variant="secondary"
              className="club-board-announce-btn"
              onClick={() => setShowAnnouncement(true)}
            >
              <FaBullhorn aria-hidden="true" />
              Post Announcement
            </YButton>
          ) : null}
          <YButton type="button" className="club-board-new-btn" onClick={() => setShowNew(true)}>
            <FaPlus aria-hidden="true" />
            New Post
          </YButton>
        </div>
      </div>

      <div className="club-board-tags" role="group" aria-label="Filter by tag">
        {tagFilters.map((tag) => {
          const isAll = tag.key === 'all';
          const cfg = isAll ? null : getBoardTagConfig(tag.key);
          const isActive = activeTag === tag.key;
          return (
            <button
              key={tag.key}
              type="button"
              className={[
                'club-board-tag-filter',
                isActive ? 'is-active' : '',
                isActive && cfg?.className ? cfg.className : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleTagFilter(tag.key)}
              aria-pressed={isActive}
            >
              {tag.label}
            </button>
          );
        })}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading && threads.length === 0 ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" className="me-2" />
          <span className="text-muted small">Loading board…</span>
        </div>
      ) : (
        <>
          <div className="club-board-posts" data-testid="club-board-posts">
            {threads.map((thread) => (
              <ClubBoardPostCard
                key={thread.id}
                thread={thread}
                clubId={clubId}
                currentUser={currentUser}
                expanded={isThreadExpanded(expandedIds, thread.id)}
                onToggleExpand={() => toggleExpand(thread.id)}
                onReplyPosted={handleReplyPosted}
              />
            ))}
            {threads.length === 0 && (
              <p className="text-muted small mb-0">
                {activeTag === 'all'
                  ? 'No posts yet. Start the conversation.'
                  : `No posts tagged “${getBoardTagConfig(activeTag).label}”. Try another filter or create one.`}
              </p>
            )}
          </div>

          <YPagination
            count={count}
            offset={offset}
            limit={limit}
            onPrev={() => setOffset(Math.max(0, offset - limit))}
            onNext={() => setOffset(offset + limit)}
            prevLabel="Prev"
            nextLabel="Next"
            ariaLabel="Board posts pages"
            dataTestId="club-board-pagination"
          />
        </>
      )}

      <YSheet
        show={showNew}
        onHide={closeNewPost}
        kicker="NEW"
        title="New Post"
        testId="club-board-new-post"
        footer={
          <>
            <YButton type="button" variant="secondary" onClick={closeNewPost}>
              Cancel
            </YButton>
            <YButton type="submit" form="club-board-new-form" disabled={creating}>
              {creating ? 'Posting…' : 'Post'}
            </YButton>
          </>
        }
      >
        <Form id="club-board-new-form" onSubmit={handleCreate}>
            <YInput
              placeholder="Title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              required
            />
            <RichTextEditorBasic
              ref={bodyEditorRef}
              value={newBody}
              onChange={setNewBody}
              placeholder="Write your message…"
            />
            <div>
              <p className="club-detail-section__subhead mb-2">Tag</p>
              <div className="club-board-new-panel__tags">
                {BOARD_TAG_KEYS.map((key) => {
                  const cfg = getBoardTagConfig(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      className={[
                        'club-board-new-tag',
                        `club-board-new-tag--${key}`,
                        newTag === key ? 'is-selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={(e) => {
                        e.preventDefault();
                        setNewTag(key);
                      }}
                      aria-pressed={newTag === key}
                    >
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <Form.Control
              type="file"
              accept="image/*"
              disabled={newImages.length >= 2 || uploading}
              onChange={(e) => handleUploadImage(e.target.files?.[0])}
            />
            <span className="small text-muted">{newImages.length}/2 images</span>
        </Form>
      </YSheet>

      <ClubAnnouncementDialog
        show={showAnnouncement}
        onHide={() => setShowAnnouncement(false)}
        clubId={clubId}
        clubName={clubName}
        memberCount={memberCount}
        onPosted={async () => {
          setActiveTag('announcement');
          syncBoardTagInUrl('announcement');
          setOffset(0);
          setExpandedIds([]);
          await fetchThreads({ tag: 'announcement', pageOffset: 0 });
          await boardService.markBoardRead(clubId);
          notifyClubBoardRead();
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="club-board-page club-board-page--embedded" data-testid="club-board-page">
        {boardMain}
      </div>
    );
  }

  return (
    <Container className="mt-3 px-2 pb-5 club-board-page" data-testid="club-board-page">
      <div className="d-flex align-items-center gap-2 mb-3">
        <YButton variant="secondary" onClick={() => navigate(`/clubs/${clubId}`)}>
          Back to Club
        </YButton>
      </div>
      {boardMain}
    </Container>
  );
}

ClubBoardPage.propTypes = {
  embedded: PropTypes.bool,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isPresident: PropTypes.bool,
  clubName: PropTypes.string,
  memberCount: PropTypes.number,
};
